import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Input, Select } from '../../components';
import { signupWithEmail, routeAfterAuth, friendlyAuthError } from '../../services/auth-service';
import { validateInvite } from '../../services/invite-service';

const CHECK_SIZES = ['Angel (<$50k)', 'Seed ($50k–$500k)', 'Series A ($500k–$5M)', 'Later stage'];

type InviteRole = 'mentor' | 'investor';

interface InviteInfo {
  role: InviteRole;
  email: string;
  name: string;
  expiresAt: string;
}

const InviteAccept: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [fundName, setFundName] = useState('');
  const [titleRole, setTitleRole] = useState('');
  const [checkSize, setCheckSize] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError('Invalid invite link.');
      setLoading(false);
      return;
    }
    validateInvite(token)
      .then(info => {
        setInviteInfo(info as InviteInfo);
        setName(info.name);
      })
      .catch(err => {
        const code = err?.code as string | undefined;
        if (code === 'INVITE_EXPIRED') setLoadError('This invite has expired. Ask your admin to send a new one.');
        else if (code === 'INVITE_NOT_PENDING') setLoadError('This invite has already been used or revoked.');
        else setLoadError('Invite link is invalid or no longer active.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Required';
    if (password.length < 6) errs.password = 'Password must be at least 6 characters';
    if (password !== confirmPassword) errs.confirmPassword = 'Passwords do not match';
    if (inviteInfo?.role === 'investor') {
      if (!fundName.trim()) errs.fundName = 'Required';
      if (!titleRole.trim()) errs.titleRole = 'Required';
      if (!checkSize) errs.checkSize = 'Required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteInfo || !token) return;
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: any = {
        name: name.trim(),
        userType: inviteInfo.role,
        inviteToken: token,
      };
      if (inviteInfo.role === 'investor') {
        payload.fundName = fundName.trim();
        payload.titleRole = titleRole.trim();
        payload.checkSize = checkSize;
      }
      const r = await signupWithEmail(inviteInfo.email, password, payload);
      navigate(routeAfterAuth(r.user, { requiresEmailVerification: r.requiresEmailVerification }));
    } catch (err: any) {
      setSubmitError(friendlyAuthError(err) || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-danger/10 flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-text">Invite Unavailable</h1>
          <p className="text-muted text-sm">{loadError}</p>
          <Link to="/login" className="inline-block text-chart text-sm hover:underline">Back to sign in</Link>
        </div>
      </div>
    );
  }

  const role = inviteInfo!.role;
  const headline = role === 'mentor' ? "You've been invited as a Mentor" : "You've been invited as an Investor";

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-line bg-shell">
        <Link to="/" className="inline-flex items-center">
          <img src="/logo.png" alt="BunyadKar" className="h-8 w-auto opacity-80" />
        </Link>
      </div>

      <div className="flex-1 flex items-start justify-center px-4 sm:px-6 py-8 sm:py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 mb-3 px-3 py-1 rounded-full bg-chart/10 text-chart text-xs font-medium tracking-wide uppercase">
              {role === 'mentor' ? 'Mentor Invite' : 'Investor Invite'}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-wide text-text uppercase mb-2">
              {headline}
            </h1>
            <p className="text-sm text-muted mt-2">
              Complete your profile to access the platform.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <Input
              label="Full Name"
              value={name}
              onChange={e => { setName(e.target.value); setErrors(p => { const n = { ...p }; delete n.name; return n; }); }}
              placeholder="Your full name"
              error={errors.name}
              maxLength={80}
            />

            <div>
              <label className="block text-sm font-medium text-text mb-1.5">Email</label>
              <input
                readOnly
                value={inviteInfo!.email}
                className="w-full bg-surface border border-line rounded-card px-3.5 py-2.5 text-text text-sm opacity-70 cursor-not-allowed"
              />
            </div>

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setErrors(p => { const n = { ...p }; delete n.password; return n; }); }}
              placeholder="At least 6 characters"
              error={errors.password}
            />

            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); setErrors(p => { const n = { ...p }; delete n.confirmPassword; return n; }); }}
              placeholder="Re-enter your password"
              error={errors.confirmPassword}
            />

            {role === 'investor' && (
              <>
                <div className="border-t border-line pt-4">
                  <p className="text-xs text-muted mb-3 uppercase tracking-wide font-medium">Fund Details</p>
                  <div className="space-y-4">
                    <Input
                      label="Fund / firm name"
                      value={fundName}
                      onChange={e => { setFundName(e.target.value); setErrors(p => { const n = { ...p }; delete n.fundName; return n; }); }}
                      placeholder="e.g. Indus Seed Partners"
                      error={errors.fundName}
                      maxLength={80}
                    />
                    <Input
                      label="Your title / role"
                      value={titleRole}
                      onChange={e => { setTitleRole(e.target.value); setErrors(p => { const n = { ...p }; delete n.titleRole; return n; }); }}
                      placeholder="e.g. Principal"
                      error={errors.titleRole}
                      maxLength={80}
                    />
                    <Select
                      label="Typical check size"
                      placeholder="Select…"
                      value={checkSize}
                      onChange={e => { setCheckSize(e.target.value); setErrors(p => { const n = { ...p }; delete n.checkSize; return n; }); }}
                      options={CHECK_SIZES.map(o => ({ value: o, label: o }))}
                      error={errors.checkSize}
                    />
                  </div>
                </div>
              </>
            )}

            {submitError && <p className="text-danger text-sm">{submitError}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-chart text-white py-3 rounded-card font-medium hover:bg-chart/90 transition-all duration-200 shadow-sm text-sm mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {submitting ? 'Setting up your account…' : 'Complete Setup'}
            </button>
          </form>

          <p className="text-center text-xs text-muted mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-chart hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default InviteAccept;
